import { Controller, useForm } from "react-hook-form";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { db } from "firebaseConfig";
import { useEffect, useState } from "react";
import { useClient } from "hooks/useClient";
import { useSite } from "hooks/useSite";
import { siteDepuisAdresse } from "utils";

interface SiteFormProps {
  onCloseModal: () => void;
  newData: (data: SiteType[]) => void;
  updateData: SiteType[] | null;
  defaultIdClient?: string;
}

type SiteFormValues = {
  idclient: string;
  url: string;
  nom: string;
  cms: SiteCms;
  actif: boolean;
  sauvegarde: SiteSauvegarde;
  motcle: string;
};

export default function SiteForm(props: SiteFormProps) {
  const current = props.updateData?.[0];
  const {
    handleSubmit,
    register,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SiteFormValues>({
    defaultValues: {
      idclient: current?.idclient ?? props.defaultIdClient ?? "",
      url: current?.url ?? "",
      nom: current?.nom ?? "",
      cms: current?.cms ?? "wordpress",
      actif: current?.actif ?? true,
      sauvegarde: current?.sauvegarde ?? "akeeba",
      motcle: current?.motcle ?? "",
    },
  });
  const { getClients } = useClient();
  const { getSites, siteHasForfaits } = useSite();
  const [clients, setClients] = useState<ClientType[]>([]);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    getClients().then(setClients).catch(console.error);
  }, [getClients]);

  async function onSubmit(values: SiteFormValues) {
    setSubmitError(false);
    const adresse = siteDepuisAdresse(values.url);
    if (!adresse) {
      setError("url", { message: "Adresse invalide, par exemple https://exemple.fr" });
      return;
    }
    try {
      // Firestore ne garantit pas l'unicité d'un champ : le domaine identifie le site
      const sites = await getSites();
      if (sites.some((site) => site.domaine === adresse.domaine && site.id !== current?.id)) {
        setError("url", { message: "Ce domaine existe déjà" });
        return;
      }
      // Les sites d'un forfait appartiennent à son client
      if (current && values.idclient !== current.idclient && (await siteHasForfaits(current.id))) {
        setError("idclient", { message: "Ce site est rattaché à des forfaits : son client ne peut pas changer" });
        return;
      }
      // Un nom resté égal au domaine suit le domaine
      const nom = values.nom.trim();
      const data = {
        idclient: values.idclient,
        nom: !nom || nom === current?.domaine ? adresse.domaine : nom,
        url: adresse.url,
        domaine: adresse.domaine,
        cms: values.cms,
        actif: Boolean(values.actif),
        sauvegarde: values.sauvegarde,
        motcle: values.motcle.trim(),
      };
      if (current) {
        await updateDoc(doc(db, "site", current.id), data);
      } else {
        await addDoc(collection(db, "site"), data);
      }
      props.onCloseModal();
      props.newData(await getSites());
    } catch (error) {
      console.error(error);
      setSubmitError(true);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl isInvalid={!!errors.idclient} mb={4}>
        <FormLabel htmlFor="idclient">Client</FormLabel>
        <Controller
          name="idclient"
          control={control}
          rules={{ required: "Champ obligatoire" }}
          render={({ field }) => (
            <Select id="idclient" placeholder="Client" {...field}>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.raison_sociale}
                </option>
              ))}
            </Select>
          )}
        />
        <FormErrorMessage>{errors.idclient?.message}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={!!errors.url} mb={4}>
        <FormLabel htmlFor="url">Adresse</FormLabel>
        <Input
          id="url"
          placeholder="https://exemple.fr"
          {...register("url", { required: "Champ obligatoire" })}
        />
        <FormErrorMessage>{errors.url?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mb={4}>
        <FormLabel htmlFor="nom">Nom</FormLabel>
        <Input id="nom" placeholder="Par défaut, le domaine" {...register("nom")} />
      </FormControl>

      <FormControl mb={4}>
        <FormLabel htmlFor="cms">CMS</FormLabel>
        <Select id="cms" {...register("cms")}>
          <option value="wordpress">WordPress</option>
          <option value="joomla">Joomla</option>
          <option value="autre">Autre</option>
        </Select>
      </FormControl>

      <FormControl mb={4}>
        <FormLabel htmlFor="sauvegarde">Sauvegarde</FormLabel>
        <Select id="sauvegarde" {...register("sauvegarde")}>
          <option value="akeeba">Akeeba, pilotée par n8n</option>
          <option value="autre">Autre méthode</option>
          <option value="aucune">Non prévue au forfait</option>
        </Select>
      </FormControl>

      <FormControl mb={4}>
        <FormLabel htmlFor="motcle">Mot-clé de la page d'accueil</FormLabel>
        <Input id="motcle" placeholder="Facultatif" {...register("motcle")} />
        <FormHelperText>
          Un texte toujours présent sur la page d'accueil. Le contrôle de disponibilité vérifie qu'il s'affiche.
        </FormHelperText>
      </FormControl>

      <FormControl mb={4}>
        <Checkbox {...register("actif")} defaultChecked={current?.actif ?? true}>
          Site actif
        </Checkbox>
      </FormControl>

      {submitError && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          L'enregistrement a échoué. Réessayez.
        </Alert>
      )}

      <Button mt={4} colorScheme="teal" isLoading={isSubmitting} type="submit" mb={4}>
        {current ? "Modifier le site" : "Ajouter le site"}
      </Button>
    </form>
  );
}
